import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ImageWithFallback from "../../../shared/imageWithFallback";
import ScreenHeader from "../../components/ScreenHeader";
import { dateLabel, historyDateLine, timeLabel } from "./dates";
import OrderDetails from "./OrderDetails";
import "./orderHistory.scss";

const ChevronLeft = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// V2 bookings list (/orderHistory) — presentational. Fetching, cancel flow and
// navigation stay in the V1 screens/orderHistory.jsx, which passes props/handlers.
function OrderHistory({ futureOrders, pastOrders, isOrthodox, defaultImg, imgFallbackSrc, onNavigate, onCancelOrder }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Details sub-view driven by ?order=f<ID> (future) / p<index> (history) so the
  // browser/hardware back button closes it naturally.
  const [searchParams, setSearchParams] = useSearchParams();
  const orderParam = searchParams.get("order");
  let selected = null;
  if (orderParam?.startsWith("f")) {
    const order = futureOrders.find((o) => String(o.ID) === orderParam.slice(1));
    if (order) selected = { order, isPast: false };
  } else if (orderParam?.startsWith("p")) {
    const order = pastOrders[Number(orderParam.slice(1))];
    if (order) selected = { order, isPast: true };
  }

  // Stale param (deep link before data arrives, or the order was just cancelled
  // and dropped by the refetch) → fall back to the list.
  const isResolved = selected !== null;
  useEffect(() => {
    if (orderParam && !isResolved) setSearchParams({}, { replace: true });
  }, [orderParam, isResolved, setSearchParams]);

  if (selected) {
    return (
      <OrderDetails
        order={selected.order}
        isPast={selected.isPast}
        isOrthodox={isOrthodox}
        defaultImg={defaultImg}
        imgFallbackSrc={imgFallbackSrc}
        onCancel={onCancelOrder}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="oh-v2">
      <ScreenHeader title={t("ORDERS_TITLE")} onBack={() => navigate(-1)} />

      <section className="oh-v2-section">
        <div className="oh-v2-section-title-row">
          <h2 className="oh-v2-section-title">{t("FUTURE_ORDERS")}</h2>
          <span className="oh-v2-count">{futureOrders.length}</span>
        </div>

        {futureOrders.length > 0 ? (
          <div className="oh-v2-carousel">
            {futureOrders.map((order) => (
              <article className="oh-v2-card" key={order.ID}>
                <div className="oh-v2-card-img">
                  <ImageWithFallback src={isOrthodox ? defaultImg : order.ClubLogo} fallbackSrc={imgFallbackSrc} alt="Club" />
                </div>
                <p className="oh-v2-card-name">{order.ClubName}</p>
                {order.Address && <p className="oh-v2-card-address">{order.Address}</p>}
                <div className="oh-v2-card-meta">
                  <div>
                    <p className="oh-v2-meta-label">{t("ORDER_DATE_LABEL")}</p>
                    <p className="oh-v2-meta-value">{dateLabel(order.SupplyDate)}</p>
                  </div>
                  <div>
                    <p className="oh-v2-meta-label">{t("ORDER_TIME_LABEL")}</p>
                    <p className="oh-v2-meta-value">
                      {timeLabel(order.SupplyDate)}
                    </p>
                  </div>
                </div>
                <div className="oh-v2-card-actions">
                  <button type="button" className="oh-v2-nav-btn" onClick={() => onNavigate(order)}>
                    {t("NAVIGATE_TO_PLACE")}
                  </button>
                  <button type="button" className="oh-v2-details-btn" onClick={() => setSearchParams({ order: `f${order.ID}` })}>
                    <ChevronLeft />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="oh-v2-empty">{t("NO_FUTURE_ORDERS")}</p>
        )}
      </section>

      <section className="oh-v2-section">
        <div className="oh-v2-section-title-row">
          <h2 className="oh-v2-section-title">{t("ORDER_HISTORY")}</h2>
          <span className="oh-v2-count">{pastOrders.length}</span>
        </div>

        {pastOrders.length > 0 ? (
          <div className="oh-v2-history-list">
            {pastOrders.map((order, i) => {
              const orderNumber = order.ID ?? order.RecordID; // field unverified — line hides when absent
              return (
                <button type="button" className="oh-v2-history-row" key={orderNumber ?? i} onClick={() => setSearchParams({ order: `p${i}` })}>
                  <div className="oh-v2-history-thumb">
                    <ImageWithFallback
                      src={isOrthodox ? defaultImg : order.MobClubLogoPath}
                      fallbackSrc={imgFallbackSrc}
                      alt="Club"
                    />
                  </div>
                  <div className="oh-v2-history-texts">
                    <p className="oh-v2-history-title">{order.LessonName || order.ClubName}</p>
                    <p className="oh-v2-history-line">{historyDateLine(order.SupplyDate)}</p>
                    {orderNumber != null && <p className="oh-v2-history-line">{t("ORDER_NUMBER", { number: orderNumber })}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="oh-v2-empty">{t("NO_PAST_ORDERS")}</p>
        )}
      </section>
    </div>
  );
}

export default OrderHistory;
